import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { revenueTargetsService } from '../../../services/growthService';

const RevenueTargetsTracker = ({ targets, onUpdateTargets, onRefresh }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTargets, setEditingTargets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Compute annual summary from real targets array
  const annualSummary = useMemo(() => {
    if (!targets?.length) return { year: new Date()?.getFullYear(), target: 0, achieved: 0, percentage: 0 };
    const annualRow = targets?.find(t => t?.quarter === 'Annual') || targets?.[0];
    const totalTarget = targets?.reduce((s, t) => s + (t?.target_amount || 0), 0);
    const totalAchieved = targets?.reduce((s, t) => s + (t?.achieved_amount || 0), 0);
    const pct = totalTarget > 0 ? Math.round((totalAchieved / totalTarget) * 100) : 0;
    return {
      year: annualRow?.year || new Date()?.getFullYear(),
      target: totalTarget,
      achieved: totalAchieved,
      percentage: Math.min(pct, 100),
    };
  }, [targets]);

  // Build quarterly breakdown from real targets
  const quarterlyBreakdown = useMemo(() => {
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const year = annualSummary?.year;
    return quarters?.map(q => {
      const row = targets?.find(t => t?.quarter === q && t?.year === year);
      const target = row?.target_amount || 0;
      const achieved = row?.achieved_amount || 0;
      const pct = target > 0 ? Math.round((achieved / target) * 100) : 0;
      return {
        id: row?.id,
        quarter: `${q} ${year}`,
        quarterKey: q,
        target,
        achieved,
        percentage: Math.min(pct, 100),
        variance: achieved - target,
      };
    });
  }, [targets, annualSummary]);

  // Monthly performance data from quarterly data
  const performanceData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months?.map((month, i) => {
      const qIdx = Math.floor(i / 3);
      const q = quarterlyBreakdown?.[qIdx];
      const monthlyTarget = q ? Math.round(q?.target / 3) : 0;
      const monthlyActual = q && q?.achieved > 0 ? Math.round(q?.achieved / 3) : 0;
      return { month, target: monthlyTarget, actual: monthlyActual };
    });
  }, [quarterlyBreakdown]);

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-success';
    if (variance < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getPerformanceStatus = (percentage) => {
    if (percentage >= 100) return { label: 'On Track', color: 'bg-success/10 text-success border-success/20' };
    if (percentage >= 75) return { label: 'Good Progress', color: 'bg-primary/10 text-primary border-primary/20' };
    if (percentage >= 50) return { label: 'Needs Attention', color: 'bg-warning/10 text-warning border-warning/20' };
    return { label: 'At Risk', color: 'bg-error/10 text-error border-error/20' };
  };

  const openEditModal = () => {
    setEditingTargets(
      quarterlyBreakdown?.map(q => ({
        id: q?.id,
        quarter: q?.quarterKey,
        year: annualSummary?.year,
        target_amount: q?.target,
        stretch_goal: targets?.find(t => t?.quarter === q?.quarterKey)?.stretch_goal || 0,
        achieved_amount: q?.achieved,
      }))
    );
    setSaveError(null);
    setShowEditModal(true);
  };

  const handleEditChange = (idx, field, value) => {
    setEditingTargets(prev => prev?.map((t, i) => i === idx ? { ...t, [field]: parseFloat(value) || 0 } : t));
  };

  const handleSaveTargets = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      for (const t of editingTargets) {
        if (t?.id) {
          await revenueTargetsService?.update(t?.id, {
            target_amount: t?.target_amount,
            stretch_goal: t?.stretch_goal,
          });
        }
      }
      onRefresh?.();
      setShowEditModal(false);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save targets.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-20">
        <p className="text-sm text-muted-foreground">Track annual and quarterly revenue performance</p>
        <Button variant="outline" iconName="Edit" iconPosition="left" iconSize={18} onClick={openEditModal}>
          Edit Targets
        </Button>
      </div>

      <div className="space-y-24">
        {/* Annual progress */}
        <div className="p-16 md:p-24 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg border border-primary/20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-16">
            <div>
              <div className="text-sm text-muted-foreground caption mb-4">Annual Target {annualSummary?.year}</div>
              <div className="text-3xl md:text-4xl font-bold text-foreground data-text mb-8">
                OMR {(annualSummary?.achieved / 1000000)?.toFixed(2)}M
              </div>
              <div className="text-sm text-muted-foreground">
                of OMR {(annualSummary?.target / 1000000)?.toFixed(2)}M target
              </div>
            </div>
            <div className="flex-1 max-w-md">
              <div className="flex items-center justify-between mb-8">
                <span className="text-sm font-medium text-foreground">Progress</span>
                <span className="text-lg font-bold text-primary data-text">{annualSummary?.percentage}%</span>
              </div>
              <div className="h-4 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-smooth" style={{ width: `${annualSummary?.percentage}%` }} />
              </div>
              <div className="mt-8 flex items-center justify-between text-xs text-muted-foreground caption">
                <span>0%</span><span>50%</span><span>100%</span>
              </div>
            </div>
            <div className={`px-16 py-12 rounded-md border ${getPerformanceStatus(annualSummary?.percentage)?.color}`}>
              <div className="flex items-center gap-8">
                <Icon name="Activity" size={16} />
                <span className="text-sm font-medium">{getPerformanceStatus(annualSummary?.percentage)?.label}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quarterly breakdown */}
        <div>
          <h3 className="text-base font-semibold text-foreground mb-16">Quarterly Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {quarterlyBreakdown?.map((quarter, index) => (
              <div key={index} className="p-16 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-smooth">
                <div className="flex items-center justify-between mb-12">
                  <span className="text-sm font-medium text-foreground">{quarter?.quarter}</span>
                  <Icon
                    name={quarter?.percentage >= 100 ? 'CheckCircle2' : 'Clock'}
                    size={18}
                    className={quarter?.percentage >= 100 ? 'text-success' : 'text-muted-foreground'}
                  />
                </div>
                <div className="space-y-8 mb-12">
                  <div>
                    <div className="text-xs text-muted-foreground caption mb-4">Target</div>
                    <div className="text-lg font-bold text-foreground data-text">OMR {(quarter?.target / 1000000)?.toFixed(2)}M</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground caption mb-4">Achieved</div>
                    <div className="text-lg font-bold text-primary data-text">OMR {(quarter?.achieved / 1000000)?.toFixed(2)}M</div>
                  </div>
                </div>
                <div className="mb-12">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-muted-foreground caption">Progress</span>
                    <span className="text-xs font-semibold text-primary data-text">{quarter?.percentage}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-smooth" style={{ width: `${quarter?.percentage}%` }} />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-12 border-t border-border">
                  <span className="text-xs text-muted-foreground caption">Variance</span>
                  <span className={`text-sm font-semibold data-text ${getVarianceColor(quarter?.variance)}`}>
                    {quarter?.variance >= 0 ? '+' : ''}OMR {(quarter?.variance / 1000)?.toFixed(0)}K
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance trend chart */}
        <div>
          <h3 className="text-base font-semibold text-foreground mb-16">Performance Trend</h3>
          <div className="h-64 md:h-80 w-full" aria-label="Revenue Performance Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="month" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} tickFormatter={(v) => `OMR ${(v / 1000)}K`} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: '8px' }}
                  formatter={(v) => `OMR ${(v / 1000)?.toFixed(0)}K`}
                />
                <Legend />
                <Bar dataKey="target" fill="var(--color-muted)" name="Target" />
                <Bar dataKey="actual" fill="var(--color-primary)" name="Actual" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Forecast note */}
        <div className="p-16 bg-primary/5 rounded-md border border-primary/20">
          <div className="flex items-start gap-12">
            <Icon name="TrendingUp" size={18} className="text-primary flex-shrink-0 mt-2" />
            <div>
              <h4 className="text-sm font-medium text-foreground mb-4">Revenue Forecast</h4>
              <p className="text-sm text-muted-foreground">
                Based on current performance, projected annual achievement is{' '}
                <span className="font-semibold text-primary">
                  OMR {((annualSummary?.achieved * (annualSummary?.target > 0 ? annualSummary?.target / Math.max(annualSummary?.achieved, 1) : 4)) / 1000000)?.toFixed(2)}M
                </span>
                {' '}({annualSummary?.percentage}% of target).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Targets Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowEditModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-24 py-20 border-b border-slate-100 flex-shrink-0">
              <h3 className="text-lg font-bold text-slate-900">Edit Revenue Targets</h3>
              <button onClick={() => setShowEditModal(false)} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-24 space-y-16 overflow-y-auto flex-1">
              {saveError && (
                <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  <Icon name="AlertCircle" size={16} />{saveError}
                </div>
              )}
              {editingTargets?.map((t, idx) => (
                <div key={idx} className="p-16 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="text-sm font-semibold text-slate-700 mb-12">{t?.quarter} {t?.year}</div>
                  <div className="grid grid-cols-2 gap-12">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-6">Target Amount ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={t?.target_amount}
                        onChange={(e) => handleEditChange(idx, 'target_amount', e?.target?.value)}
                        className="w-full px-12 py-8 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-6">Stretch Goal ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={t?.stretch_goal}
                        onChange={(e) => handleEditChange(idx, 'stretch_goal', e?.target?.value)}
                        className="w-full px-12 py-8 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                </div>
              ))}
              {editingTargets?.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-16">No quarterly targets found. Add targets first using the "Add Revenue Target" button.</p>
              )}
            </div>
            <div className="flex items-center gap-12 px-24 py-16 border-t border-slate-100 flex-shrink-0">
              <button onClick={() => setShowEditModal(false)} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button
                onClick={handleSaveTargets}
                disabled={saving || editingTargets?.length === 0}
                className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8"
              >
                {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Save" size={16} /> Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueTargetsTracker;